/**
 * Панель периода — общая для светофора руководителя и рабочего стола исполнителя.
 * Разметка и тексты дословно из макета (`#periodBar`, JS 1792–1863, 3351–3369).
 *
 * В макете панель физически переносилась между секциями `appendChild`-ом; в порте
 * она просто рендерится внутри нужной секции, а состояние периода живёт в query-строке
 * (`?days=7` либо `?from=&to=`) — см. `usePeriodState`.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import { dayWord, fmtDate, fmtISO } from '../lib/format';
import {
    DEFAULT_HISTORY_START,
    PERIOD_PRESETS,
    PERIOD_PRESET_LABELS,
    computeLocalRange,
    parsePeriod,
    periodToSearch,
    todayIso,
    type PeriodValue,
} from '../lib/deadlines';
import type { DashboardRange } from '../api/types';

export interface PeriodBarProps {
    value: PeriodValue;
    onChange: (value: PeriodValue) => void;
    /** `DashboardDto.range` — если пришёл, подпись справа берётся из него. */
    range?: DashboardRange | null;
    /** Нижняя граница произвольного периода (`min` у полей даты). */
    historyStart?: string;
    /** Верхняя граница (`max`), по умолчанию — сегодня в таймзоне клиники. */
    today?: string;
    label?: ReactNode;
    /** Дополнительные элементы панели (например, кнопка «Выгрузить отчёт»). */
    children?: ReactNode;
}

const HINT_TEXT = 'Задайте даты и нажмите «Применить»';

export function PeriodBar({
    value,
    onChange,
    range,
    historyStart = DEFAULT_HISTORY_START,
    today = todayIso(),
    label = '🗓 Период',
    children,
}: PeriodBarProps) {
    const local = useMemo(() => computeLocalRange(value), [value]);
    const [customOpen, setCustomOpen] = useState(value.mode === 'custom');
    const [from, setFrom] = useState(() => (value.mode === 'custom' ? value.from : local.from));
    const [to, setTo] = useState(() => (value.mode === 'custom' ? value.to : local.to));
    const [error, setError] = useState('');
    const [hint, setHint] = useState(false);

    // Период мог поменяться снаружи (кнопка «назад», ссылка): подтягиваем поля.
    useEffect(() => {
        if (value.mode === 'custom') {
            setCustomOpen(true);
            setFrom(value.from);
            setTo(value.to);
        }
    }, [value]);

    const selectPreset = useCallback(
        (days: number) => {
            setCustomOpen(false);
            setError('');
            setHint(false);
            onChange({ mode: 'preset', days });
        },
        [onChange],
    );

    const openCustom = useCallback(() => {
        setCustomOpen(true);
        setFrom(value.mode === 'custom' ? value.from : local.from);
        setTo(value.mode === 'custom' ? value.to : local.to);
        setError(HINT_TEXT);
        setHint(true);
    }, [local.from, local.to, value]);

    // Порядок проверок и тексты — как в макете, менять нельзя.
    const apply = useCallback(() => {
        if (!from || !to) {
            setHint(false);
            setError('Укажите обе даты');
            return;
        }
        if (from > to) {
            setHint(false);
            setError('Начало периода позже конца');
            return;
        }
        if (to > today) {
            setHint(false);
            setError('Дата не может быть позже ' + fmtDate(today));
            return;
        }
        if (from < historyStart) {
            setHint(false);
            setError('Данные доступны с ' + fmtDate(historyStart));
            return;
        }
        setError('');
        setHint(false);
        onChange({ mode: 'custom', from, to });
    }, [from, to, today, historyStart, onChange]);

    const shown = range
        ? {
              from: range.from,
              to: range.to,
              days: range.days,
              prev_from: range.prev_from,
              prev_to: range.prev_to,
          }
        : local;

    const errorClass = ['period-error', error ? 'show' : '', hint ? 'hint' : '']
        .filter(Boolean)
        .join(' ');

    return (
        <div className="period-bar">
            <span className="pb-label">{label}</span>

            <div className="seg" role="group" aria-label="Выбор периода">
                {PERIOD_PRESETS.map((days) => (
                    <button
                        key={days}
                        type="button"
                        className={value.mode === 'preset' && value.days === days ? 'active' : undefined}
                        onClick={() => selectPreset(days)}
                    >
                        {PERIOD_PRESET_LABELS[days]}
                    </button>
                ))}
                <button
                    type="button"
                    className={customOpen ? 'active' : undefined}
                    onClick={openCustom}
                >
                    Произвольный
                </button>
            </div>

            <div className={customOpen ? 'custom-range show' : 'custom-range'}>
                <span>с</span>
                <input
                    type="date"
                    value={from}
                    min={historyStart}
                    max={today}
                    aria-label="Начало периода"
                    onChange={(event) => setFrom(event.target.value)}
                />
                <span>по</span>
                <input
                    type="date"
                    value={to}
                    min={historyStart}
                    max={today}
                    aria-label="Конец периода"
                    onChange={(event) => setTo(event.target.value)}
                />
                <button type="button" className="apply" onClick={apply}>
                    Применить
                </button>
                <span className={errorClass} role={hint ? undefined : 'alert'}>
                    {error}
                </span>
            </div>

            {children}

            <div className="pb-info">
                Текущий период:{' '}
                <b>
                    {fmtDate(shown.from)} — {fmtDate(shown.to)}
                </b>{' '}
                ({shown.days} {dayWord(shown.days)})
                <br />
                <span className="vs">
                    сравнение с {fmtDate(shown.prev_from)} — {fmtDate(shown.prev_to)}
                </span>
            </div>
        </div>
    );
}

/**
 * Период в query-строке: `?days=7` либо `?from=2026-08-01&to=2026-08-31`.
 * Один и тот же хук использует и светофор, и рабочий стол — ссылка на экран
 * остаётся воспроизводимой.
 */
export function usePeriodState(): [PeriodValue, (next: PeriodValue) => void] {
    const [search, setSearch] = useSearchParams();
    const value = useMemo(() => parsePeriod(search), [search]);
    const setValue = useCallback(
        (next: PeriodValue) => {
            const params = new URLSearchParams(search);
            params.delete('days');
            params.delete('from');
            params.delete('to');
            for (const [key, item] of Object.entries(periodToSearch(next))) params.set(key, item);
            setSearch(params, { replace: true });
        },
        [search, setSearch],
    );
    return [value, setValue];
}

/** Значение для `<input type="date">` из любой даты. */
export const toDateInputValue = fmtISO;

export default PeriodBar;
